/**
 * Deterministic, client-side fallback generator for the "Keep More
 * Customers" panel that renders below the full-width Localytics Score
 * breakdown in the Business Review module.
 *
 * The panel is internal-recommendations based on the business's SWOT
 * analysis of their own reviews:
 *
 *   - "keep"    → what the business should KEEP doing, derived from the
 *                 themes their 5-star reviewers consistently praise.
 *   - "reverse" → what the business should REVERSE / fix, derived from
 *                 the recurring complaints in their negative reviews.
 *
 * The primary source for this content is Gemini (see
 * `/ai/generate-retention-plays`), but authenticated calls can fail and
 * public landing/demo pages have no Firebase auth. This builder produces
 * the same shape from the positive/negative review themes already stored
 * on the business document so the panel is never blank.
 *
 * Output shape (consumed by KeepMoreCustomersCard):
 *   {
 *     keep:    [ { action: '...', detail: '...' }, ... ],
 *     reverse: [ { action: '...', detail: '...' }, ... ],
 *   }
 */

const cleanThemes = (themes) =>
  (Array.isArray(themes) ? themes : [])
    .filter((t) => typeof t === 'string' && t.trim().length > 0)
    .map((t) => t.trim());

const titleCase = (str) => {
  const s = (str || '').trim();
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const KEEP_FALLBACK = [
  {
    action: 'Protect what your best reviews praise',
    detail:
      'Read your last 20 five-star reviews and list the specific things customers rave about. Turn those into a standard every team member follows on every job.',
  },
  {
    action: 'Feature your happiest customers',
    detail:
      "Pull your strongest 5-star quotes onto your homepage and Google Business Profile so new customers see what existing ones love before they call.",
  },
];

const REVERSE_FALLBACK = [
  {
    action: 'Audit your recent 1-star reviews',
    detail:
      'Read every review under 4 stars from the last 90 days, group the complaints by theme, and fix the most common root cause first.',
  },
  {
    action: 'Respond to every negative review',
    detail:
      'Reply publicly to each low-rated review within 48 hours with a specific fix — future customers judge you by how you handle problems.',
  },
];

/**
 * Build the "Keep More Customers" retention plays deterministically from
 * the business's positive and negative review themes.
 *
 * @param {Object} params
 * @param {string[]} [params.positiveThemes] - common_positive_themes
 * @param {string[]} [params.negativeThemes] - common_negative_themes
 * @returns {{ keep: Array<{action:string, detail:string}>,
 *             reverse: Array<{action:string, detail:string}> }}
 */
export function buildKeepMoreCustomers({ positiveThemes, negativeThemes } = {}) {
  const positives = cleanThemes(positiveThemes).slice(0, 3);
  const negatives = cleanThemes(negativeThemes).slice(0, 3);

  const keep = positives.map((theme) => ({
    action: `Keep delivering on ${theme}`,
    detail: `Customers consistently praise your ${theme} in 5-star reviews. Make it a repeatable standard — train new staff on it and highlight it in your marketing so it keeps winning repeat business.`,
  }));

  const reverse = negatives.map((theme) => ({
    action: `Reverse ${theme} complaints`,
    detail: `${titleCase(theme)} shows up as a recurring theme in your negative reviews. Fix the root cause this month and respond publicly to every affected review to stop it from driving customers away.`,
  }));

  // Guarantee at least a couple of items per column so the panel is never
  // lopsided or empty when a business has thin theme data.
  let keepIdx = 0;
  while (keep.length < 2 && keepIdx < KEEP_FALLBACK.length) {
    keep.push(KEEP_FALLBACK[keepIdx]);
    keepIdx += 1;
  }
  let reverseIdx = 0;
  while (reverse.length < 2 && reverseIdx < REVERSE_FALLBACK.length) {
    reverse.push(REVERSE_FALLBACK[reverseIdx]);
    reverseIdx += 1;
  }

  return {
    keep: keep.slice(0, 3),
    reverse: reverse.slice(0, 3),
  };
}

const keepMoreCustomersBuilder = { buildKeepMoreCustomers };

export default keepMoreCustomersBuilder;
